import React, { useId } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import Radio from './Radio';
import FieldError from '../FormComponents/FieldError';
import {
  FIELD_LABEL_CLASS,
  FIELD_REQUIRED_CLASS,
  FIELD_HELP_CLASS,
  FIELD_GAP,
} from './FieldShell';

/**
 * A set of radios that share a `name` and a label.
 *
 * The `name` matters more than it looks: without a shared one the browser treats
 * each input as its own group, so arrow keys stop moving between options and two
 * of them can be checked at once. Callers rarely remember to pass it, so the
 * group generates one.
 *
 * `<fieldset>`/`<legend>` rather than a div and a label - that is what makes a
 * screen reader announce the question before each answer.
 */

export interface RadioGroupOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface RadioGroupProps {
  label?: string;
  options: RadioGroupOption[];
  value?: string;
  onChange?: (value: string) => void;
  /** Shared radio `name`. Generated when omitted. */
  name?: string;
  required?: boolean;
  description?: string;
  error?: string;
  helpText?: string;
  disabled?: boolean;
  /** Lay the options out in a row instead of a column. */
  orientation?: 'vertical' | 'horizontal';
  className?: string;
}

const RadioGroup: React.FC<RadioGroupProps> = ({
  label,
  options,
  value,
  onChange,
  name,
  required,
  description,
  error,
  helpText,
  disabled,
  orientation = 'vertical',
  className,
}) => {
  const { t } = useTranslation();
  const generatedName = useId();
  const groupName = name || generatedName;

  return (
    <fieldset className={clsx('flex-1 min-w-0', className)}>
      {label && (
        <legend className={clsx(FIELD_GAP, FIELD_LABEL_CLASS)}>
          {t(label)}
          {required && <span className={FIELD_REQUIRED_CLASS}>*</span>}
        </legend>
      )}
      {description && <p className={clsx(FIELD_GAP, FIELD_HELP_CLASS)}>{t(description)}</p>}

      <div
        className={clsx(
          orientation === 'horizontal' ? 'flex flex-wrap items-start gap-x-6 gap-y-2' : 'space-y-2'
        )}
      >
        {options.map((option) => (
          <Radio
            key={option.value}
            name={groupName}
            value={option.value}
            label={option.label}
            description={option.description}
            checked={value === option.value}
            disabled={disabled || option.disabled}
            error={!!error}
            onChange={onChange}
          />
        ))}
      </div>

      <FieldError error={error} />

      {helpText && !error && <p className={clsx('mt-1.5', FIELD_HELP_CLASS)}>{t(helpText)}</p>}
    </fieldset>
  );
};

export default RadioGroup;
